import React, { useMemo, useRef } from "react";

import { DEFAULT_CROP_RECT, DEFAULT_IMAGE_FRAME } from "../../../../../components/shared/imageToolbar/imageCropModel";

import {

  aspectFromSize,

  computeFittedStageSize,

  TOOL_PREP_STAGE_INSET,

  useImageNaturalSize,

  useToolPrepPreviewViewport,

} from "../../../lib/toolPrepImageLayout";

import {

  getSourceCropRect,

  resolveCropPrepAspectRatio,

} from "../../../lib/cropPrepModel";

import RoundLoader from "../../../../../components/ui/RoundLoader";

import CropOverlay from "./CropOverlay";

import SoftWatermarkOverlay from "../../../shared/Popup/SoftWatermarkOverlay";

import ToolPrepCanvasStage from "../../../shared/Popup/ToolPrepCanvasStage";

import "./CropPreview.css";



const EMPTY_STAGE_ASPECT = 1;



function fitContainSize(srcW, srcH, maxW, maxH) {

  const w = Math.max(1, Number(srcW) || 0);

  const h = Math.max(1, Number(srcH) || 0);

  if (!w || !h) {

    return { width: maxW, height: maxH };

  }

  const scale = Math.min(maxW / w, maxH / h, 1);

  return {

    width: Math.max(24, Math.round(w * scale)),

    height: Math.max(24, Math.round(h * scale)),

  };

}



const CropPreview = ({

  imageUrl = null,

  settings = null,

  loading = false,

  applying = false,

  resultMode = false,

  resultImageUrl = null,

  emptyLabel = "Upload an image to preview",

  onFramesChange = () => {},

  disabled = false,

  circleMode = false,

  softWatermarkToolKey = "crop",

}) => {

  const stageRef = useRef(null);

  const naturalSize = useImageNaturalSize(imageUrl);

  const resultNaturalSize = useImageNaturalSize(resultMode ? resultImageUrl : null);

  const viewport = useToolPrepPreviewViewport(stageRef);



  const cropFrame = settings?.cropFrame || DEFAULT_CROP_RECT;

  const imageFrame = settings?.imageFrame || DEFAULT_IMAGE_FRAME;

  const freeform = !circleMode && settings?.preset === "freeform";



  const stageSize = useMemo(() => {

    const vw = viewport.width;

    const vh = viewport.height;

    if (!vw || !vh) {

      return !imageUrl && !resultImageUrl

        ? { width: 420, height: 420 }

        : { width: 0, height: 0 };

    }



    if (!imageUrl && !resultImageUrl) {

      return (

        computeFittedStageSize(vw, vh, EMPTY_STAGE_ASPECT, {

          inset: TOOL_PREP_STAGE_INSET,

        }) ?? { width: 420, height: 420 }

      );

    }



    if (resultMode && resultImageUrl) {

      const aspect = aspectFromSize(

        resultNaturalSize.width,

        resultNaturalSize.height,

        EMPTY_STAGE_ASPECT

      );

      return (

        computeFittedStageSize(vw, vh, aspect, {

          inset: TOOL_PREP_STAGE_INSET,

        }) ?? { width: 0, height: 0 }

      );

    }



    const imageAspect = aspectFromSize(

      naturalSize.width,

      naturalSize.height,

      EMPTY_STAGE_ASPECT

    );



    return (

      computeFittedStageSize(vw, vh, imageAspect, {

        inset: TOOL_PREP_STAGE_INSET,

      }) ?? { width: 0, height: 0 }

    );

  }, [

    imageUrl,

    naturalSize.height,

    naturalSize.width,

    resultImageUrl,

    resultMode,

    resultNaturalSize.height,

    resultNaturalSize.width,

    viewport.height,

    viewport.width,

  ]);



  const overlaySize = useMemo(() => {

    if (!stageSize.width || !stageSize.height) {

      return { width: 0, height: 0 };

    }

    if (resultMode && resultImageUrl) {

      return fitContainSize(

        resultNaturalSize.width || stageSize.width,

        resultNaturalSize.height || stageSize.height,

        stageSize.width,

        stageSize.height

      );

    }

    return fitContainSize(

      naturalSize.width || stageSize.width,

      naturalSize.height || stageSize.height,

      stageSize.width,

      stageSize.height

    );

  }, [

    naturalSize.height,

    naturalSize.width,

    resultImageUrl,

    resultMode,

    resultNaturalSize.height,

    resultNaturalSize.width,

    stageSize.height,

    stageSize.width,

  ]);



  const aspectRatio = useMemo(() => {

    if (circleMode && overlaySize.width > 0 && overlaySize.height > 0) {

      return overlaySize.height / overlaySize.width;

    }

    return resolveCropPrepAspectRatio(

      settings,

      naturalSize.width,

      naturalSize.height

    );

  }, [

    circleMode,

    naturalSize.height,

    naturalSize.width,

    overlaySize.height,

    overlaySize.width,

    settings,

  ]);



  const showEditOverlay = !resultMode && imageUrl && overlaySize.width;

  const showResult = resultMode && resultImageUrl && overlaySize.width;



  return (

    <ToolPrepCanvasStage

      className="crop-preview"

      ref={stageRef}

      metaImageUrl={imageUrl}

      metaWidth={

        resultMode && resultNaturalSize.width

          ? resultNaturalSize.width

          : Math.round(

              (getSourceCropRect(settings)?.w || 1) * (naturalSize.width || 0)

            ) || naturalSize.width

      }

      metaHeight={

        resultMode && resultNaturalSize.height

          ? resultNaturalSize.height

          : Math.round(

              (getSourceCropRect(settings)?.h || 1) * (naturalSize.height || 0)

            ) || naturalSize.height

      }

    >

      <div

        className={`crop-preview__stage${

          resultMode ? " crop-preview__stage--result" : ""

        }`}

        style={{

          width: stageSize.width || undefined,

          height: stageSize.height || undefined,

        }}

      >

        {loading ? (

          <div className="crop-preview__loading">

            <RoundLoader size="md" label="Removing background…" />

          </div>

        ) : applying ? (

          <div className="crop-preview__loading">

            <RoundLoader size="md" label="Applying crop…" />

          </div>

        ) : showResult ? (

          <>

            <img

              src={resultImageUrl}

              alt="Cropped result"

              className="crop-preview__result"

              style={{

                width: overlaySize.width,

                height: overlaySize.height,

              }}

              draggable={false}

            />

            <SoftWatermarkOverlay toolKey={softWatermarkToolKey} />

          </>

        ) : showEditOverlay ? (

          <CropOverlay

            imageUrl={imageUrl}

            cropFrame={cropFrame}

            imageFrame={imageFrame}

            aspectRatio={aspectRatio}

            freeform={freeform}

            circleMode={circleMode}

            zoom={settings?.zoom}

            width={overlaySize.width}

            height={overlaySize.height}

            onFramesChange={onFramesChange}

            disabled={disabled}

          />

        ) : (

          <div className="crop-preview__empty">{emptyLabel}</div>

        )}

      </div>


    </ToolPrepCanvasStage>

  );

};



export default CropPreview;

